import React, { useState, useEffect, forwardRef } from "react";
import { FaPlay, FaPause, FaExpand, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toggleMute } from "../../redux/user/userReducer";

const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
};

const VideoControls = forwardRef(({ className = "", isPost, handleFullscreen, videoDuration }, ref) => {
    const videoRef = ref;
    const dispatch = useDispatch()
    const muted = useSelector((state) => state.user.muted)
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(videoDuration || 0);
    const [buffered, setBuffered] = useState(0);
    const [volume, setVolume] = useState(0);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [lastMouseMoveTime, setLastMouseMoveTime] = useState(Date.now());
    const [userPaused, setUserPaused] = useState(false)

    useEffect(() => {
        const video = videoRef?.current;
        if (!video) return;

        const updateTime = () => setCurrentTime(video.currentTime);
        const updateMetadata = () => {
            if (video.duration > 0) setDuration(video.duration);
            setVolume(video.volume);
        };
        const updatePlayState = () => setIsPlaying(!video.paused);
        const updateBuffered = () => {
            if (video.buffered.length) {
                setBuffered(video.buffered.end(video.buffered.length - 1));
            }
        };

        video.addEventListener("timeupdate", updateTime);
        video.addEventListener("loadedmetadata", updateMetadata);
        video.addEventListener("play", updatePlayState);
        video.addEventListener("pause", updatePlayState);
        video.addEventListener("progress", updateBuffered);

        return () => {
            video.removeEventListener("timeupdate", updateTime);
            video.removeEventListener("loadedmetadata", updateMetadata);
            video.removeEventListener("play", updatePlayState);
            video.removeEventListener("pause", updatePlayState);
            video.removeEventListener("progress", updateBuffered);
        };
    }, [videoRef]);

    useEffect(() => {
        if (!isPlaying) return;

        let timeout;
        const handleMouseMove = () => {
            setIsVisible(true);
            setLastMouseMoveTime(Date.now());
        };

        const checkInactivity = () => {
            const now = Date.now();
            if (now - lastMouseMoveTime > 2500) {
                setIsVisible(false);
            } else {
                setIsVisible(true);
            }
        };

        document.addEventListener("mousemove", handleMouseMove);
        timeout = setInterval(checkInactivity, 500);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            clearInterval(timeout);
        };
    }, [lastMouseMoveTime, isPlaying]);

    const handlePlayPause = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        if (!video) return;
        if (video.paused) {
            setUserPaused(false)
            video.play();
        } else {
            setUserPaused(true)
            video.pause();
        }
    };

    const handleProgressClick = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        if (!video) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percent = clickX / rect.width;
        video.currentTime = percent * duration;
    };

    const handleProgressDrag = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        if (!video) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const moveX = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(1, moveX / rect.width));
        video.currentTime = percent * duration;
    };

    const handleToggleMute = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        if (!video) return;
        video.muted = !video.muted;
        dispatch(toggleMute(video.muted))
    };

    const handleVolumeChange = (e) => {
        e.stopPropagation();
        const video = videoRef?.current;
        if (!video) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickY = e.clientY - rect.top;
        const height = rect.height;
        const volumeLevel = 1 - clickY / height;
        video.volume = volumeLevel;
        video.muted = false;
        setVolume(volumeLevel);
    };

    const onFullScreenRequest = (e) => {
        e.stopPropagation()
        if (isPost) {
            handleFullscreen()
            return
        }
        const video = videoRef?.current;
        if (video?.requestFullscreen) {
            video.requestFullscreen();
        }
    };
    const videoEnded = currentTime !== 0 && currentTime === duration

    return (
        <div
            className={`absolute inset-0 p-4 transition-all duration-[400ms] ${(videoEnded || muted || userPaused) ? '' : isVisible ? 'pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 opacity-0' : 'pointer-events-none opacity-0'} bg-gradient-to-t from-black/70 to-transparent text-white text-sm ${className} flex flex-col justify-end`}
        >

            <div onClick={(e) => e.stopPropagation()} className="flex flex-col w-full pb-2 pt-4">
                {/* Progress Bar */}
                {!muted && duration > 0 &&
                    <div
                        className="w-full h-1 bg-white/20 cursor-pointer mb-2 rounded relative"
                        onClick={handleProgressClick}
                        onMouseMove={(e) => {
                            e.stopPropagation();
                            if (e.buttons === 1) handleProgressDrag(e)
                        }}
                    >
                        <div
                            className="absolute top-0 left-0 h-full bg-white/40 rounded"
                            style={{ width: `${(buffered / duration) * 100 || 0}%` }}
                        />
                        <div
                            className="absolute top-0 left-0 h-full bg-white rounded transition-all"
                            style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                        />
                    </div>
                }


                {/* Controls */}
                <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-between relative">
                    <div className="flex items-center gap-4">
                        <button onClick={handlePlayPause} className="focus:outline-none">
                            {isPlaying ? <FaPause /> : <FaPlay />}
                        </button>
                        <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                    </div>


                    {!muted &&
                        <div className="relative w-14 flex flex-col overflow-visible">

                            <div
                                className={`absolute ${showVolumeSlider ? 'bg-customGray shadow-sm shadow-black/60' : ""} rounded-full pb-3 pt-2 left-0 -bottom-3 flex flex-col items-center gap-1 px-2 h-28 duration-300 transition-all ease-in`}
                                onMouseLeave={(e) => { e.stopPropagation(); setShowVolumeSlider(false) }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {showVolumeSlider && (
                                    <div
                                        className="relative w-2 h-20 bg-customDark rounded-full overflow-hidden mt-auto cursor-pointer"
                                        onClick={handleVolumeChange}
                                        onMouseMove={(e) => {
                                            e.stopPropagation()
                                            if (e.buttons === 1) handleVolumeChange(e)
                                        }}
                                    >
                                        <div
                                            className="absolute bottom-0 left-0 right-0 bg-white"
                                            style={{ height: `${volume * 100}%` }}
                                        />
                                    </div>
                                )}

                                <button
                                    onClick={handleToggleMute}
                                    onMouseEnter={() => setShowVolumeSlider(true)}
                                    className="focus:outline-none mt-auto"
                                >
                                    {volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                                </button>
                            </div>

                            <button onClick={onFullScreenRequest} className="self-end focus:outline-none ">
                                <FaExpand />
                            </button>

                        </div>
                    }
                </div>


                {(muted && volume <= 0) &&
                    <div className="self-end z-10">
                        <div className="animate-pulse bg-black/70 text-white px-3 py-1 rounded-md text-sm shadow-md">
                            🔇 Tap video to unmute
                        </div>
                    </div>
                }

            </div>
        </div>
    );
});

export default VideoControls;
